/**
 * Journey page: Memory & History (J10 + J11)
 *
 * Peak state: returning user — session restored, memory nodes surfaced,
 * agent referencing prior context via MemoryBadge.
 * - J10: MemoryFrame showing knowledge graph, MemoryBadge floating near TextFrame
 * - J11: Sidebar shows past sessions, HistoryPanel in right panel
 */

import { useState, useRef, useEffect } from "react";
import { useCanvasTransform } from "@/workshop/components/canvas/canvas-viewport";
import { WorkshopLayoutProvider } from "@/workshop/components/layout/layout-provider";
import { WorkshopMasterLayout } from "@/workshop/components/layout/master-layout";
import { MemoryBadge } from "@/workshop/components/canvas/memory-badge";
import { MemoryFrame } from "@/workshop/components/shapes/memory-frame/memory-frame";
import { TextFrame } from "@/workshop/components/shapes/text-frame/text-frame";

// ─── Types ────────────────────────────────────────────────────────────────────

type Pos = { x: number; y: number };

// ─── Initial frame positions ──────────────────────────────────────────────────

const INITIAL_POS = {
  memory: { x: 290, y: 60 } as Pos,
  text: { x: 690, y: 70 } as Pos,
};

// ─── Canvas content ───────────────────────────────────────────────────────────

function CanvasContent() {
  // --- positions & drag ---
  const [pos, setPos] = useState(INITIAL_POS);
  const drag = useRef<{
    key: string;
    sx: number;
    sy: number;
    px: number;
    py: number;
  } | null>(null);
  const { zoom, isPanMode } = useCanvasTransform();
  const dotR = `${1 / zoom}px`;
  const dotSpacing = `${24 / zoom}px`;
  const zoomRef = useRef(zoom);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!drag.current) return;
      const { key, sx, sy, px, py } = drag.current;
      setPos((p) => ({
        ...p,
        [key]: {
          x: px + (e.clientX - sx) / zoomRef.current,
          y: py + (e.clientY - sy) / zoomRef.current,
        },
      }));
    };
    const up = () => {
      drag.current = null;
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, []);

  function grab(key: string, e: React.MouseEvent) {
    if (isPanMode) return;
    e.preventDefault();
    drag.current = {
      key,
      sx: e.clientX,
      sy: e.clientY,
      px: pos[key as keyof typeof pos].x,
      py: pos[key as keyof typeof pos].y,
    };
  }

  return (
    <div
      className="relative h-full w-full overflow-hidden bg-white dark:bg-black"
      data-testid="memory-history-canvas"
      style={{
        backgroundImage: `radial-gradient(circle, var(--color-canvas-dot) ${dotR}, transparent ${dotR})`,
        backgroundSize: `${dotSpacing} ${dotSpacing}`,
      }}
    >
      {/* Session restored watermark */}
      <div
        className="pointer-events-none absolute inset-x-0 top-3 z-10 flex justify-center"
        aria-hidden="true"
        data-testid="session-restored-watermark"
      >
        <span className="text-[11px] font-medium tracking-widest text-primary opacity-20 uppercase">
          Session restored
        </span>
      </div>

      {/* MemoryFrame — knowledge graph */}
      <div
        className="absolute z-10 cursor-grab active:cursor-grabbing select-none"
        style={{ left: pos.memory.x, top: pos.memory.y, width: 340 }}
        onMouseDown={(e) => grab("memory", e)}
        data-testid="canvas-frame-memory"
      >
        <MemoryFrame
          title="Knowledge Graph"
          scenario="medium"
          agentName="Aria"
          data-testid="memory-frame-main"
        />
      </div>

      {/* MemoryBadge — moves with TextFrame */}
      <div
        className="absolute z-20"
        style={{ left: pos.text.x, top: pos.text.y - 34 }}
        data-testid="canvas-memory-badge"
      >
        <MemoryBadge count={3} agentName="Aria" />
      </div>

      {/* TextFrame — Last Week's Draft, done */}
      <div
        className="absolute z-10 cursor-grab active:cursor-grabbing select-none"
        style={{ left: pos.text.x, top: pos.text.y, width: 260 }}
        onMouseDown={(e) => grab("text", e)}
        data-testid="canvas-frame-text"
      >
        <TextFrame
          title="Last Week's Draft"
          text={
            "## AI Content Marketing in 2026\n\nThe shift from single-agent to multi-agent pipelines has been the defining change of the past year. Teams using coordinated AI agents report 3\xd7 output with the same headcount.\n\n### Key Takeaways\n- Delegation via @mention is the new task management\n- Memory nodes eliminate repeated briefings\n- Agent specialisation beats generalist models"
          }
          status="done"
          agentName="Aria"
        />
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function MemoryHistoryPage() {
  return (
    <WorkshopLayoutProvider defaultSidebarOpen defaultRightPanelOpen>
      <svg style={{ display: "none" }} aria-hidden="true">
        <defs>
          <filter
            id="liquid-glass-distortion"
            x="-10%"
            y="-10%"
            width="120%"
            height="120%"
            colorInterpolationFilters="sRGB"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.018 0.014"
              numOctaves="3"
              seed="5"
              result="noise"
            />
            <feColorMatrix
              type="saturate"
              values="0"
              in="noise"
              result="grayNoise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="grayNoise"
              scale="2"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>
      <div
        className="h-screen w-screen overflow-hidden"
        data-testid="memory-history-page"
      >
        <WorkshopMasterLayout>
          <CanvasContent />
        </WorkshopMasterLayout>
      </div>
    </WorkshopLayoutProvider>
  );
}
